import { Link } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  useSEO,
  useStructuredData,
  generateFAQStructuredData,
  mergeKeywordSets,
  COMPETITIVE_KEYWORD_CLUSTERS,
} from '../lib/seo';
import {
  ArrowRight,
  Brain,
  HeartHandshake,
  LineChart,
  MessageCircle,
  Shield,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { stockUrl } from '../lib/marketing-stock';
import { TensionRiMicroCoachShowcase } from '../components/marketing/TensionRiMicroCoachShowcase';

const pageFaqs = [
  {
    question: 'What is Relationship Intelligence in TENSION?',
    answer:
      'It is the flagship workflow inside TENSION that turns daily signals into shared context for couples: where tension diverges between partners, what helped after rough patches, and safer wording before messages go out—plus optional summaries for therapy.',
  },
  {
    question: 'How is Relationship Intelligence different from quiz-style couples apps?',
    answer:
      'Quiz-first apps optimize for engagement and content variety. Relationship Intelligence optimizes for early awareness, repair, and message-level coaching so small frictions are visible before they become recurring fights.',
  },
  {
    question: 'Do I need a Pro plan to try Relationship Intelligence?',
    answer:
      'You can explore the suite on a standard plan to learn the workflow. Pro unlocks deeper pattern history and advanced signals when you are ready to commit after you have an account and see ongoing value.',
  },
  {
    question: 'Is Relationship Intelligence private?',
    answer:
      'Pairing requires explicit consent. The suite emphasizes trends and coaching—not exposing private journal text. You choose what to export for therapy.',
  },
];

export default function FeatureRelationshipIntelligence() {
  useSEO({
    title: 'Relationship Intelligence Suite | TENSION',
    description:
      'TENSION’s Relationship Intelligence suite helps couples see tension gaps early, replay what worked after ruptures, coach messages before sending, and export concise trends for therapy—built for prevention, not just prompts.',
    keywords: mergeKeywordSets(
      COMPETITIVE_KEYWORD_CLUSTERS.core as unknown as string[],
      COMPETITIVE_KEYWORD_CLUSTERS.communication as unknown as string[],
      COMPETITIVE_KEYWORD_CLUSTERS.conflict as unknown as string[],
      [
        'relationship intelligence',
        'couples tension tracking',
        'rupture repair couples',
        'therapy prep export',
        'relationship analytics couples',
      ],
    ),
    canonical: '/features/relationship-intelligence',
    ogType: 'website',
    twitterCard: 'summary_large_image',
  });

  useStructuredData(generateFAQStructuredData(pageFaqs));

  return (
    <div className="relative min-h-screen overflow-hidden bg-neutral-950">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_50%_-15%,rgba(139,92,246,0.42),transparent_52%),radial-gradient(ellipse_90%_55%_at_100%_40%,rgba(236,72,153,0.18),transparent_48%),radial-gradient(ellipse_70%_50%_at_0%_85%,rgba(59,130,246,0.12),transparent_42%)]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:48px_48px] opacity-35"
        aria-hidden
      />

      <div className="relative mx-auto max-w-6xl px-4 py-10 md:py-14">
        <header className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.06] shadow-[0_25px_80px_-25px_rgba(124,58,237,0.55)] backdrop-blur-xl">
          <div className="grid gap-8 p-8 md:grid-cols-[1.15fr_1fr] md:items-center md:gap-12 md:p-12">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-violet-500/20 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-violet-200 ring-1 ring-violet-400/35">
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
                Flagship suite
              </p>
              <h1 className="mt-4 text-4xl font-black tracking-tight text-white md:text-5xl lg:text-[3.25rem] lg:leading-[1.1]">
                Relationship Intelligence that reads the room—before the room blows up
              </h1>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-neutral-300 md:text-lg">
                Most couples apps optimize for daily fun. TENSION’s Relationship Intelligence optimizes for{' '}
                <span className="text-white">early signals</span>, <span className="text-white">repair habits</span>,
                and <span className="text-white">clearer messages</span> so you fix patterns—not just survive the week.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/home#relationship-intelligence">
                  <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 px-6 text-white shadow-lg shadow-violet-900/40 hover:from-violet-500 hover:to-fuchsia-500">
                    Open the suite <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button
                    variant="outline"
                    className="border-white/25 bg-white/5 text-white hover:bg-white/10"
                  >
                    Compare plans
                  </Button>
                </Link>
              </div>
              <p className="mt-4 text-xs text-neutral-500">
                Sign in required. Use core workflows first; upgrade to Pro when you want deeper history and advanced
                signals.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              <TensionRiMicroCoachShowcase variant="dark" />

              <div className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-violet-950/90 via-neutral-900/90 to-fuchsia-950/70 p-6 shadow-inner ring-1 ring-white/5 md:p-8">
                <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-fuchsia-500/25 blur-3xl" aria-hidden />
                <Brain className="mb-4 h-10 w-10 text-violet-300" aria-hidden />
                <h2 className="text-lg font-bold text-white">Why this page exists</h2>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400">
                  Searchers land here looking for <em className="text-neutral-200 not-italic">what</em> Relationship
                  Intelligence is and <em className="text-neutral-200 not-italic">why</em> it matters. Below is the full
                  story—how it works, what you get, and how it fits next to quiz-heavy couples apps. Technical definitions
                  for each module live in the{' '}
                  <Link to="/relationship-intelligence/faq" className="text-violet-300 underline-offset-2 hover:underline">
                    Relationship Intelligence FAQ
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>
        </header>

        <article className="mt-12 space-y-12">
          <section aria-labelledby="what-is-ri" className="max-w-3xl">
            <h2 id="what-is-ri" className="text-2xl font-bold tracking-tight text-white md:text-3xl">
              What Relationship Intelligence actually is
            </h2>
            <div className="mt-4 space-y-4 text-neutral-300 leading-relaxed">
              <p>
                “Relationship intelligence” here means <strong className="font-semibold text-neutral-100">shared
                situational awareness</strong>: not a single score, but a loop—capture signal, spot mismatch, try a
                repair, learn what worked, and get language help before the next hard message. It is designed for
                couples who want fewer surprise escalations and more repeatable calm.
              </p>
              <p>
                That loop sits on top of consent-based pairing. The product is built to surface{' '}
                <strong className="font-semibold text-neutral-100">trends and coaching</strong>, not to turn your private
                notes into a feed. When therapy is part of your stack, you can export a compact summary instead of
                re-explaining six months of conflict from memory.
              </p>
            </div>
          </section>

          <figure className="overflow-hidden rounded-2xl border border-white/10 shadow-xl ring-1 ring-violet-500/15">
            <img
              src={stockUrl('coupleOutdoor', 1600)}
              alt="Two people standing close together, relaxed and connected"
              width={1600}
              height={900}
              loading="lazy"
              decoding="async"
              className="aspect-[21/9] w-full object-cover object-center min-h-[140px] max-h-[min(320px,40vh)]"
            />
            <figcaption className="border-t border-white/10 bg-black/30 px-4 py-2 text-center text-[11px] text-neutral-500">
              Stock imagery — represents emotional closeness, not product screenshots
            </figcaption>
          </figure>

          <section aria-labelledby="how-loop">
            <div className="mb-6">
              <h2 id="how-loop" className="text-2xl font-bold tracking-tight text-white md:text-3xl">
                How the loop works
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-neutral-400">
                Three beats you can explain to a partner in one sentence each.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {[
                {
                  icon: TrendingUp,
                  title: 'See the gap',
                  body: 'Compare tension signals between partners so you notice divergence before stories harden.',
                },
                {
                  icon: LineChart,
                  title: 'Replay repair',
                  body: 'After spikes, highlight what correlated with coming back together—so you repeat what works.',
                },
                {
                  icon: MessageCircle,
                  title: 'Coach the message',
                  body: 'Draft risky texts with escalation-aware rewrites before they land in the thread.',
                },
              ].map(({ icon: Icon, title, body }) => (
                <Card
                  key={title}
                  className="border-white/10 bg-white/[0.04] p-6 backdrop-blur-md transition hover:border-violet-500/30 hover:bg-white/[0.06]"
                >
                  <Icon className="h-8 w-8 text-violet-400" aria-hidden />
                  <h3 className="mt-4 font-semibold text-white">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-400">{body}</p>
                </Card>
              ))}
            </div>
          </section>

          <section aria-labelledby="suite-modules">
            <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <h2 id="suite-modules" className="text-2xl font-bold tracking-tight text-white md:text-3xl">
                  Inside the suite
                </h2>
                <p className="mt-2 max-w-2xl text-sm text-neutral-400">
                  Each module supports the loop. For glossary-style definitions (e.g. attachment drift, silent signals),
                  use the FAQ—this section stays focused on outcomes.
                </p>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Card className="border-violet-500/20 bg-gradient-to-br from-violet-950/50 to-neutral-950/80 p-6 md:p-7">
                <h3 className="text-lg font-semibold text-white">Two-Person Tension Delta</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-300">
                  Surfaces when you and your partner are pulling different directions on stress—so you can name it early
                  instead of debating who felt it first.
                </p>
              </Card>
              <Card className="border-fuchsia-500/20 bg-gradient-to-br from-fuchsia-950/40 to-neutral-950/80 p-6 md:p-7">
                <h3 className="text-lg font-semibold text-white">Rupture-to-Repair Replay</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-300">
                  Connects rough patches with recovery: a personal timeline of “what was different when we found our way
                  back.”
                </p>
              </Card>
              <Card className="border-white/10 bg-white/[0.04] p-6 md:p-7">
                <h3 className="text-lg font-semibold text-white">Conversation Micro-Coach</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-300">
                  Message-scope AI: risk on the draft, calmer rewrites, same intent—aligned with{' '}
                  <Link
                    to="/features/conversation-micro-coach"
                    className="text-violet-300 underline-offset-2 hover:underline"
                  >
                    the Micro-Coach feature page
                  </Link>
                  .
                </p>
              </Card>
              <Card className="border-white/10 bg-white/[0.04] p-6 md:p-7">
                <h3 className="text-lg font-semibold text-white">Therapist Bridge Export</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-300">
                  Bring a concise snapshot into sessions: triggers, helpful moves, and near-term risk windows—without
                  pasting chat logs.
                </p>
              </Card>
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-[1fr_1fr]">
            <Card className="border-emerald-500/25 bg-gradient-to-br from-emerald-950/40 to-neutral-950/90 p-6 md:p-8">
              <Shield className="h-10 w-10 text-emerald-400/90" aria-hidden />
              <h2 className="mt-4 text-xl font-bold text-white">Privacy and consent</h2>
              <p className="mt-2 text-sm leading-relaxed text-neutral-300">
                Pairing is opt-in. The suite is designed around visibility into <em className="not-italic text-neutral-200">patterns</em>, not
                broadcasting diary entries. Exports are deliberate—you decide what crosses into therapy or coaching.
              </p>
            </Card>
            <Card className="border-violet-500/30 bg-gradient-to-br from-violet-900/35 to-neutral-950/90 p-6 md:p-8">
              <HeartHandshake className="h-10 w-10 text-violet-300" aria-hidden />
              <h2 className="mt-4 text-xl font-bold text-white">Pro when the value is obvious</h2>
              <p className="mt-2 text-sm leading-relaxed text-neutral-300">
                Create an account, run the core workflow, and see whether the loop changes how you argue. When you want
                longer history, richer pattern depth, and advanced signals,{' '}
                <Link to="/pricing" className="font-medium text-violet-200 underline-offset-2 hover:underline">
                  Pro is there on pricing
                </Link>
                —not as a wall on day one, but as a clear step once you trust the product.
              </p>
            </Card>
          </section>

          <section aria-labelledby="ri-faq" className="rounded-2xl border border-white/10 bg-black/25 p-6 md:p-10">
            <h2 id="ri-faq" className="text-xl font-bold text-white md:text-2xl">
              Common questions
            </h2>
            <ul className="mt-6 space-y-6">
              {pageFaqs.map((f) => (
                <li key={f.question}>
                  <h3 className="font-semibold text-neutral-100">{f.question}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-400">{f.answer}</p>
                </li>
              ))}
            </ul>
          </section>

          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-8">
            <div className="flex flex-wrap gap-3">
              <Link to="/relationship-intelligence/faq">
                <Button variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10">
                  Full FAQ
                </Button>
              </Link>
              <Link to="/compare/tension-vs-paired">
                <Button variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10">
                  TENSION vs Paired
                </Button>
              </Link>
              <Link to="/guides">
                <Button variant="outline" className="border-white/20 bg-white/5 text-white hover:bg-white/10">
                  Guides
                </Button>
              </Link>
            </div>
            <Link
              to="/home#relationship-intelligence"
              className="inline-flex items-center gap-1 text-sm font-medium text-violet-300 hover:text-violet-200"
            >
              Go to Relationship Intelligence <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
}
